import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Sparkles, Trash2, Pencil, Download, Search, Plus, MapPin, Compass } from 'lucide-react';
import { toast } from 'sonner';
import { fmtKg, statusBadgeClass, downloadCsv } from './_utils';
import ReadOnlyBanner from './ReadOnlyBanner';

export default function Bookings() {
  const ctx = useOutletContext() || {};
  const canEdit = ctx.canEditModule ? ctx.canEditModule('routeops_bookings') : true;
  const [bookings, setBookings] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    business_name: '', postcode: '', date_required: '',
    pallets: 1, weight_kg: '', picker_id: '',
  });
  const [suggestion, setSuggestion] = useState(null);
  const [suggestedRoute, setSuggestedRoute] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [depotPostcode, setDepotPostcode] = useState('');
  const [depotInput, setDepotInput] = useState('');
  const [depotSaving, setDepotSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [b, r, u, s] = await Promise.all([
        api.get('/routeops/bookings'),
        api.get('/routeops/routes'),
        api.get('/routeops/users'),
        api.get('/routeops/settings'),
      ]);
      setBookings(b.data || []);
      setRoutes(r.data || []);
      setUsers(u.data || []);
      setSettings(s.data || {});
      if (s.data?.depot_postcode) {
        setDepotPostcode(s.data.depot_postcode);
        setDepotInput(s.data.depot_postcode);
      }
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSetDepot = async () => {
    if (!depotInput.trim()) return;
    setDepotSaving(true);
    try {
      const res = await api.post('/routeops/settings/set-depot', { postcode: depotInput.trim() });
      setDepotPostcode(res.data.postcode);
      toast.success(`Depot set to ${res.data.postcode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to set depot');
    } finally {
      setDepotSaving(false);
    }
  };

  // UK postcode regex — only fire suggest-route once the input looks complete
  const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

  // Auto-suggest route when postcode changes
  const handlePostcodeChange = async (pc) => {
    setForm(f => ({ ...f, postcode: pc }));
    setSuggestedRoute(null);
    if (depotPostcode && UK_POSTCODE_RE.test(pc.trim())) {
      try {
        const res = await api.post('/routeops/bookings/suggest-route', { postcode: pc.trim() });
        setSuggestedRoute(res.data.suggested_route);
      } catch { /* ignore */ }
    }
  };

  // Auto-suggestion
  useEffect(() => {
    const { postcode, date_required, weight_kg } = form;
    if (!postcode || !date_required || !weight_kg) {
      setSuggestion(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.post('/routeops/planner/suggest', {
          postcode, date: date_required, weight_kg: Number(weight_kg),
        });
        setSuggestion(res.data.suggestion);
      } catch {
        setSuggestion(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.postcode, form.date_required, form.weight_kg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.postcode || !form.date_required) {
      toast.error('All fields required');
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/routeops/bookings', {
        ...form,
        pallets: Number(form.pallets),
        weight_kg: Number(form.weight_kg) || 0,
        picker_id: form.picker_id || null,
        // Persist the route the system suggested at creation time so it stays
        // visible on the Bookings table even after a manual route assignment.
        suggested_route: suggestedRoute || null,
      });
      toast.success('Booking created');
      setForm({ business_name: '', postcode: '', date_required: '', pallets: 1, weight_kg: '', picker_id: '' });
      setSuggestion(null);
      fetchAll();
    } catch (e) {
      toast.error('Create failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking? Full snapshot will be written to audit trail.')) return;
    try {
      await api.delete(`/routeops/bookings/${id}`);
      toast.success('Booking deleted');
      fetchAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/routeops/bookings/${editing.id}`, {
        business_name: editing.business_name,
        postcode: editing.postcode,
        date_required: editing.date_required,
        pallets: Number(editing.pallets),
        weight_kg: Number(editing.weight_kg) || 0,
        route_key: editing.route_key || null,
        picker_id: editing.picker_id || null,
      });
      toast.success('Saved');
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const filtered = useMemo(() => {
    return bookings.filter(b => {
      if (search && !b.business_name.toLowerCase().includes(search.toLowerCase())) return false;
      if (dateFilter && b.date_required !== dateFilter) return false;
      return true;
    });
  }, [bookings, search, dateFilter]);

  const routeName = (key) => routes.find(r => r.key === key)?.name || '—';

  const handleExportCsv = () => {
    downloadCsv(filtered,
      [
        { label: 'Business', key: 'business_name' },
        { label: 'Postcode', key: 'postcode' },
        { label: 'Date', key: 'date_required' },
        { label: 'Pallets', key: 'pallets' },
        { label: 'Weight (KG)', key: 'weight_kg' },
        { label: 'Route', get: r => routeName(r.route_key) },
        { label: 'Suggested Route', get: r => r.suggested_route ? routeName(r.suggested_route) : '' },
        { label: 'Status', key: 'status' },
      ],
      'routeops-bookings.csv',
    );
    toast.success('CSV exported');
  };

  return (
    <div data-testid="routeops-bookings-page">
      <div className="flex items-center gap-3 pb-3 mb-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Bookings</h1>
        <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Delivery Intake</span>
        <div className="flex gap-2 ml-2">
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            {bookings.filter(b => b.status === 'Unassigned').length} Unassigned
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            {bookings.filter(b => b.status === 'Assigned').length} Assigned
          </Badge>
        </div>
      </div>

      {!canEdit && <ReadOnlyBanner pageName="Bookings" />}

      {/* Depot Location Bar */}
      <Card className="mb-4">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-3 flex-wrap">
            <MapPin className="h-4 w-4 shrink-0" style={{ color: 'var(--amber)' }} />
            <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)' }}>Depot</span>
            {depotPostcode ? (
              <Badge variant="outline" className="text-xs" style={{ color: 'var(--green)', borderColor: 'var(--green)' }} data-testid="depot-badge">
                {depotPostcode}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}>Not set</Badge>
            )}
            <div className="flex items-center gap-1.5 ml-auto">
              <Input
                value={depotInput}
                onChange={(e) => setDepotInput(e.target.value.toUpperCase())}
                placeholder="Postcode e.g. B6 5AD"
                className="h-8 w-36 text-xs"
                data-testid="depot-input"
                disabled={!canEdit}
                onKeyDown={(e) => e.key === 'Enter' && canEdit && handleSetDepot()}
              />
              <Button size="sm" className="h-8 text-xs" onClick={handleSetDepot} disabled={depotSaving || !canEdit}
                style={{ background: 'var(--amber)', color: 'var(--bg-void)' }} data-testid="depot-set-btn">
                {depotSaving ? '...' : 'Set'}
              </Button>
            </div>
          </div>
          {!depotPostcode && (
            <p className="text-[10px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
              Set your depot to enable auto NE/SE/SW/NW route suggestions for delivery postcodes.
            </p>
          )}
        </CardContent>
      </Card>

      <div className={`grid grid-cols-1 ${canEdit ? 'lg:grid-cols-[320px_1fr]' : ''} gap-4`}>
        {/* Sticky form — hidden for view-only users */}
        {canEdit && (
        <Card className="self-start lg:sticky lg:top-36">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-amber-500" /> New Booking
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label className="text-xs">Business Name</Label>
                <Input
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  placeholder="e.g. Lidl Distribution Centre"
                  data-testid="booking-business-name"
                />
              </div>
              <div>
                <Label className="text-xs">Postcode</Label>
                <Input
                  value={form.postcode}
                  onChange={(e) => handlePostcodeChange(e.target.value.toUpperCase())}
                  placeholder="LE4 8NA"
                  data-testid="booking-postcode"
                />
                {suggestedRoute && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Compass className="h-3 w-3" style={{ color: 'var(--amber)' }} />
                    <span className="text-[10px] font-semibold" style={{ color: 'var(--amber)' }}>
                      Suggested route: {routes.find(r => r.key === suggestedRoute)?.name || suggestedRoute.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Date Required</Label>
                <Input
                  type="date"
                  value={form.date_required}
                  onChange={(e) => setForm({ ...form, date_required: e.target.value })}
                  data-testid="booking-date"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Pallets</Label>
                  <Input
                    type="number" min="1"
                    value={form.pallets}
                    onChange={(e) => setForm({ ...form, pallets: e.target.value })}
                    data-testid="booking-pallets"
                  />
                </div>
                <div>
                  <Label className="text-xs">Weight (KG)</Label>
                  <Input
                    type="number" min="0"
                    value={form.weight_kg}
                    onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                    placeholder="Optional"
                    data-testid="booking-weight"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Picker</Label>
                <select
                  value={form.picker_id}
                  onChange={(e) => setForm({ ...form, picker_id: e.target.value })}
                  className="w-full h-9 rounded-md border px-3 text-sm"
                  style={{ background: 'var(--bg-void)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="booking-picker"
                >
                  <option value="">No picker assigned</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.full_name} ({u.role})
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-amber-500 hover:bg-amber-600"
                data-testid="booking-submit"
              >
                <Plus className="h-4 w-4 mr-1" />
                {submitting ? 'Saving…' : 'Book Delivery'}
              </Button>

              {suggestion && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs" data-testid="booking-suggestion">
                  <div className="font-bold text-amber-700 uppercase text-[10px] tracking-wide mb-1 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Auto-suggestion
                  </div>
                  <div className="text-slate-700">
                    Weight <strong>{fmtKg(Number(form.weight_kg))}</strong> + postcode <strong>{form.postcode}</strong> → suggest{' '}
                    <strong className="text-emerald-700">{suggestion.route_name}</strong>
                    {suggestion.driver_name && <> with {suggestion.driver_name}</>}
                    {' '}({fmtKg(suggestion.remaining_kg)} remaining)
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
        )}

        {/* Table */}
        <Card>
          <CardHeader className="flex-row flex items-center gap-2 space-y-0 pb-3">
            <CardTitle className="text-base">All Bookings</CardTitle>
            <div className="flex-1" />
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search business…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7 h-8 w-44 text-xs"
                data-testid="bookings-search"
              />
            </div>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-8 w-36 text-xs"
              data-testid="bookings-date-filter"
            />
            <Button
              variant="outline" size="sm"
              onClick={handleExportCsv}
              data-testid="bookings-export-csv"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> CSV
            </Button>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Business</TableHead>
                    <TableHead>Postcode</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Plt</TableHead>
                    <TableHead className="text-right">Weight</TableHead>
                    <TableHead>Picker</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Suggested</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && <TableRow><TableCell colSpan={10} className="text-center text-slate-400">Loading…</TableCell></TableRow>}
                  {!loading && filtered.length === 0 && (
                    <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-6">No bookings</TableCell></TableRow>
                  )}
                  {filtered.map(b => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                      <TableCell className="font-medium text-slate-900">{b.business_name}</TableCell>
                      <TableCell className="font-mono text-xs">{b.postcode}</TableCell>
                      <TableCell className="font-mono text-xs">{b.date_required}</TableCell>
                      <TableCell className="text-right font-mono">{b.pallets}</TableCell>
                      <TableCell className="text-right font-mono">{b.weight_kg ? fmtKg(b.weight_kg) : <span style={{ color: 'var(--text-muted)' }}>TBD</span>}</TableCell>
                      <TableCell className="text-xs">{b.picker_name || '—'}</TableCell>
                      <TableCell className="text-xs">{routeName(b.route_key)}</TableCell>
                      <TableCell className="text-xs" data-testid={`booking-suggested-${b.id}`}>
                        {b.suggested_route ? (
                          (() => {
                            const sameAsAssigned = b.route_key && b.route_key === b.suggested_route;
                            return (
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] uppercase font-mono border rounded px-1.5 py-0.5 ${
                                  sameAsAssigned
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-blue-50 text-blue-700 border-blue-200'
                                }`}
                                title={sameAsAssigned ? 'Matches assigned route' : 'AI suggested a different route'}
                              >
                                {sameAsAssigned && '✓ '}
                                {routeName(b.suggested_route)}
                              </span>
                            );
                          })()
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`text-[10px] uppercase font-mono border rounded px-1.5 py-0.5 ${statusBadgeClass(b.status)}`}>
                          {b.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {canEdit ? (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost" size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => setEditing({ ...b })}
                              data-testid={`booking-edit-${b.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                              onClick={() => handleDelete(b.id)}
                              data-testid={`booking-delete-${b.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="booking-edit-modal">
          <DialogHeader>
            <DialogTitle>Edit Booking</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Business Name</Label>
                <Input value={editing.business_name} onChange={e => setEditing({ ...editing, business_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Postcode</Label>
                  <Input value={editing.postcode} onChange={e => setEditing({ ...editing, postcode: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={editing.date_required} onChange={e => setEditing({ ...editing, date_required: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Pallets</Label>
                  <Input type="number" value={editing.pallets} onChange={e => setEditing({ ...editing, pallets: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Weight (KG)</Label>
                  <Input type="number" value={editing.weight_kg} onChange={e => setEditing({ ...editing, weight_kg: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Route</Label>
                <select
                  className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
                  value={editing.route_key || ''}
                  onChange={e => setEditing({ ...editing, route_key: e.target.value || null })}
                >
                  <option value="">Unassigned</option>
                  {routes.map(r => <option key={r.key} value={r.key}>{r.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={handleSaveEdit} data-testid="booking-edit-save">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
